import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  StatTile,
  Tooltip,
  formatEuro,
  toast,
  type Column,
} from '@elbklar/ui';

import { SeitenKopf } from '@/components/layout/SeitenKopf';
import { INTERVALLE, RecurringItemDialog } from './RecurringItemDialog';
import { categoriesApi, recurringApi } from '@/lib/api';
import { ZeilenMenue } from '@/components/ZeilenMenue';
import { satzText } from '@/lib/steuer';
import type { RecurringItem } from '@/types';

const TEILER: Record<string, number> = { monthly: 1, quarterly: 3, yearly: 12 };

export function FixkostenPage() {
  const queryClient = useQueryClient();

  const [dialogOffen, setDialogOffen] = useState(false);
  const [inBearbeitung, setInBearbeitung] = useState<RecurringItem | null>(null);
  const [zeigeInaktive, setZeigeInaktive] = useState(false);
  const [zuLoeschen, setZuLoeschen] = useState<RecurringItem | null>(null);

  const posten = useQuery({
    queryKey: ['recurring', zeigeInaktive],
    queryFn: () => recurringApi.list(zeigeInaktive),
  });

  const kategorien = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
  });

  const aktualisieren = () => {
    void queryClient.invalidateQueries({ queryKey: ['recurring'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
    void queryClient.invalidateQueries({ queryKey: ['projection'] });
  };

  const umschalten = useMutation({
    mutationFn: ({ id, aktiv }: { id: string; aktiv: boolean }) => recurringApi.update(id, { is_active: aktiv }),
    onSuccess: aktualisieren,
    onError: () => toast.error('Umschalten fehlgeschlagen'),
  });

  // Der Fehler erscheint im Bestätigungsdialog, der dafür offen bleibt.
  const loeschen = useMutation({
    mutationFn: (id: string) => recurringApi.remove(id),
    onSuccess: () => {
      toast.info('Posten gelöscht');
      aktualisieren();
    },
  });

  const oeffneDialog = (eintrag: RecurringItem | null) => {
    setInBearbeitung(eintrag);
    setDialogOffen(true);
  };

  const liste = posten.data ?? [];
  const aktive = liste.filter((p) => p.is_active);

  // Auf den Monat umgelegt — sonst springt die Summe je nach Intervall.
  const proMonat = (p: RecurringItem, netto = false) =>
    Number(netto ? (p.net_amount ?? p.amount) : p.amount) / (TEILER[p.interval] ?? 1);

  const monatlich = aktive.reduce((summe, p) => summe + proMonat(p), 0);
  const monatlichNetto = aktive.reduce((summe, p) => summe + proMonat(p, true), 0);
  const mitSteuer = Math.round(monatlich) !== Math.round(monatlichNetto);

  const kategorieName = (id: string | null) => (id ? kategorien.data?.find((k) => k.id === id)?.name : undefined);

  const spalten: Column<RecurringItem>[] = [
    {
      key: 'name',
      header: 'Name',
      strong: true,
      mobil: 'titel',
      render: (p) => (
        <span className={p.is_active ? undefined : 'text-muted-foreground'}>
          {p.name}
          {!p.is_active && <span className="ml-2 t-small font-normal">stillgelegt</span>}
        </span>
      ),
    },
    { key: 'kategorie', header: 'Kategorie', mobil: 'unter', render: (p) => kategorieName(p.category_id) ?? '–' },
    {
      key: 'rhythmus',
      header: 'Rhythmus',
      mobil: 'unter',
      render: (p) => (
        <>
          {INTERVALLE.find((i) => i.value === p.interval)?.label}
          <span className="text-muted-foreground"> · am {p.day_of_month}.</span>
        </>
      ),
    },
    {
      key: 'betrag',
      header: 'Betrag',
      align: 'end',
      mono: true,
      mobil: 'wert',
      render: (p) => formatEuro(p.amount, { cent: true }),
      // Am Handy fällt die Knopfspalte weg; die Aktionen stehen hinter „…“ am Betrag.
      renderMobil: (p) => (
        <span className="flex items-center gap-1">
          {formatEuro(p.amount, { cent: true })}
          <ZeilenMenue
            label={`Aktionen für ${p.name}`}
            aktionen={[
              { label: 'Bearbeiten', icon: Pencil, onSelect: () => oeffneDialog(p) },
              { label: p.is_active ? 'Stilllegen' : 'Aktivieren', icon: Power, onSelect: () => umschalten.mutate({ id: p.id, aktiv: !p.is_active }) },
              { label: 'Löschen', icon: Trash2, gefahr: true, onSelect: () => setZuLoeschen(p) },
            ]}
          />
        </span>
      ),
    },
    {
      key: 'ust',
      header: 'USt.',
      align: 'end',
      mono: true,
      render: (p) =>
        Number(p.tax_rate) > 0 ? (
          <Tooltip content={`${formatEuro(p.net_amount, { cent: true })} netto`}>
            <span>{satzText(p.tax_rate)}</span>
          </Tooltip>
        ) : (
          satzText(p.tax_rate)
        ),
    },
    { key: 'monat', header: 'pro Monat', align: 'end', mono: true, render: (p) => formatEuro(proMonat(p), { cent: true }) },
    {
      key: 'aktionen',
      header: '',
      align: 'end',
      width: 120,
      render: (p) => (
        <div className="flex justify-end gap-1">
          <IconButton icon={Pencil} label="Bearbeiten" variant="ghost" size="sm" onClick={() => oeffneDialog(p)} />
          <IconButton
            icon={Power}
            label={p.is_active ? 'Stilllegen' : 'Aktivieren'}
            variant="ghost"
            size="sm"
            onClick={() => umschalten.mutate({ id: p.id, aktiv: !p.is_active })}
          />
          <IconButton icon={Trash2} label="Löschen" variant="ghost" size="sm" onClick={() => setZuLoeschen(p)} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <SeitenKopf
        title="Fixkosten"
        subtitle="Alles, was regelmäßig läuft. Quartals- und Jahresposten werden für die Monatssumme umgelegt."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => oeffneDialog(null)}>
            Posten anlegen
          </Button>
        }
      />

      <StatTile
        label="Pro Monat"
        value={
          monatlich < 0 ? (
            formatEuro(monatlich, { cent: true })
          ) : (
            <span className="text-success">{formatEuro(monatlich, { cent: true })}</span>
          )
        }
        hint={
          <>
            {mitSteuer && (
              <>
                <span className="tabular">{formatEuro(monatlichNetto, { cent: true })}</span> netto ·{' '}
              </>
            )}
            {aktive.length} aktive Posten. Die Kreditrate zählt separat und steht unter „Kredit“.
          </>
        }
      />

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="t-title">Posten</h2>
          <Checkbox label="Stillgelegte anzeigen" checked={zeigeInaktive} onChange={setZeigeInaktive} />
        </div>
        <DataTable
          columns={spalten}
          rows={liste}
          loading={posten.isLoading}
          label="Fixkosten"
          empty={
            <EmptyState
              icon={CalendarClock}
              title="Noch keine Fixkosten"
              description="Regelmäßige Kosten erscheinen hier. Ohne sie ist der Plan-Burn null und die Projektion zeigt zu wenig Abfluss."
            />
          }
        />
      </section>

      <RecurringItemDialog
        offen={dialogOffen}
        onSchliessen={() => setDialogOffen(false)}
        kategorien={kategorien.data ?? []}
        onGespeichert={aktualisieren}
        posten={inBearbeitung}
      />

      <ConfirmDialog
        open={zuLoeschen !== null}
        onOpenChange={(offen) => !offen && setZuLoeschen(null)}
        title="Posten löschen"
        description={
          zuLoeschen
            ? `„${zuLoeschen.name}“ wird gelöscht und fließt nicht mehr in die Projektion. Wer ihn nur pausieren will, legt ihn still.`
            : undefined
        }
        confirmLabel="Posten löschen"
        tone="danger"
        onConfirm={() => (zuLoeschen ? loeschen.mutateAsync(zuLoeschen.id) : undefined)}
      />
    </div>
  );
}
